import { useEffect, useMemo, useState, useCallback } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  RefreshCw, Search, AlertOctagon, Eye, Mail, BadgeCheck, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const FILTROS = [
  { key: "pendentes", label: "Pendentes" },
  { key: "aprovadas", label: "Aprovadas" },
  { key: "todas", label: "Todas" },
];

function today() { return new Date().toISOString().slice(0, 10); }
function firstDayOfMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function AtrasoBadge({ dias }) {
  const d = Number(dias || 0);
  const tone = d > 30 ? "danger" : d > 7 ? "warn" : "neutral";
  const cls = {
    danger: "bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25",
    warn: "bg-[#FFF4D6] text-[#8B6A1F] border border-[#8B6A1F]/25",
    neutral: "bg-stone-100 text-stone-700 border border-stone-300",
  }[tone];
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${cls}`}>
      {d} {d === 1 ? "dia" : "dias"}
    </span>
  );
}

export default function DivergenciasFinanceiras() {
  const [me, setMe] = useState(null);
  const [pousadas, setPousadas] = useState([]);

  const [filtros, setFiltros] = useState({
    filtro: "pendentes",
    pousada_id: "all",
    data_inicio: firstDayOfMonth(),
    data_fim: today(),
    busca: "",
  });

  const [lista, setLista] = useState({ data: null, loading: true, error: null });

  const [detalheAlvo, setDetalheAlvo] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [cobrancas, setCobrancas] = useState([]);
  const [cobrancasLoading, setCobrancasLoading] = useState(false);

  const [aprovarAlvo, setAprovarAlvo] = useState(null);
  const [aprovarMotivo, setAprovarMotivo] = useState("");
  const [aprovando, setAprovando] = useState(false);

  const [cobrarAlvo, setCobrarAlvo] = useState(null);
  const [cobrarObs, setCobrarObs] = useState("");
  const [cobrando, setCobrando] = useState(false);

  const podeAprovar = !!(me && me.is_admin); // /aprovar-desconto = api_router (admin-only)
  const podeCobrar = !!(me && (me.is_admin || me.is_finance)); // /cobrar = finance_api

  useEffect(() => {
    (async () => {
      try {
        const [u, po] = await Promise.all([
          api.get("/auth/me").catch(() => ({ data: null })),
          api.get("/pousadas-list").catch(() => ({ data: [] })),
        ]);
        setMe(u.data);
        setPousadas(Array.isArray(po.data) ? po.data.filter((x) => x.id) : []);
      } catch { /* silencioso */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLista((s) => ({ ...s, loading: true, error: null }));
    try {
      const params = { filtro: filtros.filtro };
      if (filtros.pousada_id !== "all") params.pousada_id = filtros.pousada_id;
      if (filtros.data_inicio) params.data_inicio = filtros.data_inicio;
      if (filtros.data_fim) params.data_fim = filtros.data_fim;
      if (filtros.busca.trim()) params.busca = filtros.busca.trim();
      const { data } = await api.get("/divergencias", { params });
      setLista({ data, loading: false, error: null });
    } catch (err) {
      setLista({ data: null, loading: false, error: err?.response?.status === 401 ? "auth" : "erro" });
    }
  }, [filtros]);

  useEffect(() => { load(); }, [load]);

  const itens = lista.data?.itens || [];

  const totais = useMemo(() => {
    let saldo = 0, aprovadas = 0, pendentes = 0;
    itens.forEach((i) => {
      saldo += Number(i.saldo || 0);
      if (i.desconto_aprovado) aprovadas += 1; else pendentes += 1;
    });
    return { total: itens.length, saldo, aprovadas, pendentes };
  }, [itens]);

  const abrirDetalhe = async (item) => {
    setDetalheAlvo(item); setDetalheOpen(true); setCobrancasLoading(true);
    setCobrancas([]);
    try {
      const { data } = await api.get(`/divergencias/${item.reserva_id}/cobrancas`);
      setCobrancas(Array.isArray(data) ? data : []);
    } catch { /* silencioso */ }
    finally { setCobrancasLoading(false); }
  };

  const executarAprovar = async () => {
    if (!aprovarAlvo) return;
    if (!aprovarMotivo.trim()) return toast.error("Motivo obrigatório");
    setAprovando(true);
    try {
      await api.post(`/divergencias/${aprovarAlvo.reserva_id}/aprovar-desconto`, {
        motivo: aprovarMotivo.trim(),
      });
      toast.success("Desconto aprovado (auditado)");
      setAprovarAlvo(null); setAprovarMotivo("");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao aprovar desconto";
      toast.error(String(msg));
    } finally {
      setAprovando(false);
    }
  };

  const executarCobrar = async () => {
    if (!cobrarAlvo) return;
    if (!cobrarAlvo.hospede_email || !cobrarAlvo.hospede_email.includes("@")) {
      return toast.error("E-mail do hóspede ausente ou inválido. Atualize o cadastro antes de cobrar.");
    }
    setCobrando(true);
    try {
      const body = cobrarObs.trim() ? { observacao: cobrarObs.trim() } : {};
      await api.post(`/divergencias/${cobrarAlvo.reserva_id}/cobrar`, body);
      toast.success("Cobrança enviada por e-mail");
      setCobrarAlvo(null); setCobrarObs("");
      if (detalheOpen && detalheAlvo?.reserva_id === cobrarAlvo.reserva_id) {
        abrirDetalhe(detalheAlvo);
      }
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao enviar cobrança";
      toast.error(String(msg));
    } finally {
      setCobrando(false);
    }
  };

  return (
    <div data-testid="page-divergencias-financeiras">
      <PageHeader
        eyebrow="Financeiro · Divergências"
        title="Divergências / Cobranças"
        description="Reservas com saldo em aberto após o check-out. Aprovar desconto grava marcador auditável; cobrança envia e-mail oficial. Reservas e pagamentos não são alterados."
        actions={
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh-divergencias">
            <RefreshCw size={14} /> Atualizar
          </Button>
        }
      />

      {lista.error === "auth" && (
        <div className="bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25 rounded-lg px-4 py-2 text-sm mb-4">
          Sessão expirada. Faça login novamente.
        </div>
      )}

      {/* Filtros oficiais suportados pelo backend */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Filtro</Label>
          <Select value={filtros.filtro} onValueChange={(v) => setFiltros({ ...filtros, filtro: v })}>
            <SelectTrigger data-testid="filter-divergencias-filtro" className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FILTROS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Pousada</Label>
          <Select value={filtros.pousada_id} onValueChange={(v) => setFiltros({ ...filtros, pousada_id: v })}>
            <SelectTrigger data-testid="filter-divergencias-pousada" className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Check-out de</Label>
          <Input type="date" data-testid="filter-divergencias-de" value={filtros.data_inicio}
            onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Até</Label>
          <Input type="date" data-testid="filter-divergencias-ate" value={filtros.data_fim}
            onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })} className="w-[150px]" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Busca (hóspede/reserva)</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={filtros.busca} data-testid="filter-divergencias-busca"
              onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
              placeholder="Nome ou nº da reserva..." className="pl-8" />
          </div>
        </div>
      </div>

      {/* KPIs derivados de dados oficiais retornados */}
      {lista.data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div data-testid="kpi-divergencias-total" className="bg-[#FFFFFF] border border-stone-200 rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest text-stone-500">Divergências no filtro</div>
            <div className="font-serif text-xl tabular-nums">{lista.data.total_reservas || totais.total}</div>
          </div>
          <div data-testid="kpi-divergencias-saldo" className="bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25 rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest opacity-70">Total pendente (oficial)</div>
            <div className="font-serif text-xl tabular-nums">{brl(lista.data.total_pendente || 0)}</div>
          </div>
          <div data-testid="kpi-divergencias-pendentes" className="bg-stone-50 border border-stone-200 rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest text-stone-500">Sem desconto aprovado</div>
            <div className="font-serif text-xl tabular-nums">{totais.pendentes}</div>
          </div>
          <div data-testid="kpi-divergencias-aprovadas" className="bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20 rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest opacity-70">Com desconto aprovado</div>
            <div className="font-serif text-xl tabular-nums">{totais.aprovadas}</div>
          </div>
        </div>
      )}

      {/* Corpo */}
      {lista.loading ? (
        <div data-testid="divergencias-loading" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando divergências...
        </div>
      ) : lista.error && lista.error !== "auth" ? (
        <div data-testid="divergencias-erro" className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
          Não foi possível carregar as divergências.
        </div>
      ) : itens.length === 0 ? (
        <div data-testid="divergencias-empty" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EAF0EA] flex items-center justify-center mx-auto mb-4 text-[#083A6B]">
            <AlertOctagon size={22} strokeWidth={1.5} />
          </div>
          <div className="font-serif text-2xl text-stone-900 mb-2">Nenhuma divergência encontrada.</div>
          <p className="text-sm text-stone-600 max-w-md mx-auto">
            Reservas quitadas, futuras ou canceladas não aparecem aqui. Ajuste o filtro se necessário.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile */}
          <div className="md:hidden space-y-3">
            {itens.map((i) => (
              <div key={i.reserva_id} data-testid={`divergencia-card-${i.reserva_id}`}
                className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-stone-900 truncate">{i.hospede_nome || "—"}</div>
                    <div className="text-xs text-stone-500">
                      {i.pousada_nome} · saiu em {fmtDate(i.data_saida)}
                    </div>
                  </div>
                  <AtrasoBadge dias={i.dias_atraso} />
                </div>
                <div className="text-xs text-stone-600 space-y-0.5 mb-3">
                  <div>Contratado: <b className="tabular-nums">{brl(i.valor_contratado)}</b></div>
                  <div>Recebido: <b className="tabular-nums text-[#083A6B]">{brl(i.valor_recebido)}</b></div>
                  <div>Saldo: <b className="tabular-nums text-[#D4A652]">{brl(i.saldo)}</b></div>
                  {i.desconto_aprovado && (
                    <div className="text-[10px] text-[#083A6B]">
                      <BadgeCheck size={11} className="inline" /> Desconto aprovado por {i.desconto_aprovado.aprovado_por_nome || i.desconto_aprovado.aprovado_por}
                    </div>
                  )}
                  {i.empresa_nome && <div className="text-[10px] text-stone-500">Empresa: {i.empresa_nome}</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => abrirDetalhe(i)}
                    data-testid={`btn-divergencia-detalhe-${i.reserva_id}`}><Eye size={14} /> Detalhes</Button>
                  {podeAprovar && !i.desconto_aprovado && (
                    <Button size="sm" variant="outline" onClick={() => { setAprovarAlvo(i); setAprovarMotivo(""); }}
                      data-testid={`btn-divergencia-aprovar-${i.reserva_id}`}>
                      <BadgeCheck size={14} /> Aprovar desconto
                    </Button>
                  )}
                  {podeCobrar && (
                    <Button size="sm" variant="outline" onClick={() => { setCobrarAlvo(i); setCobrarObs(""); }}
                      data-testid={`btn-divergencia-cobrar-${i.reserva_id}`}>
                      <Mail size={14} /> Cobrar
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop */}
          <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Saída</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Atraso</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Hóspede</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Contratado</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Recebido</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Saldo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Situação</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.map((i) => (
                  <TableRow key={i.reserva_id} data-testid={`divergencia-row-${i.reserva_id}`}>
                    <TableCell className="text-stone-700 text-sm whitespace-nowrap">{fmtDate(i.data_saida)}</TableCell>
                    <TableCell><AtrasoBadge dias={i.dias_atraso} /></TableCell>
                    <TableCell className="text-stone-900 text-sm">
                      <div className="truncate max-w-[220px]">{i.hospede_nome || "—"}</div>
                      {i.hospede_email && <div className="text-[10px] text-stone-500 truncate">{i.hospede_email}</div>}
                    </TableCell>
                    <TableCell className="text-stone-700 text-sm">{i.pousada_nome || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{brl(i.valor_contratado)}</TableCell>
                    <TableCell className="text-right tabular-nums text-[#083A6B]">{brl(i.valor_recebido)}</TableCell>
                    <TableCell className="text-right tabular-nums text-[#D4A652]">{brl(i.saldo)}</TableCell>
                    <TableCell className="text-xs">
                      {i.desconto_aprovado ? (
                        <span className="inline-flex items-center gap-1 text-[#083A6B]">
                          <BadgeCheck size={12} /> Desconto aprovado
                        </span>
                      ) : (
                        <span className="text-[#7c3a20]">Pendente</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(i)}
                          aria-label="Ver detalhes" data-testid={`btn-divergencia-detalhe-${i.reserva_id}`}>
                          <Eye size={14} />
                        </Button>
                        {podeAprovar && !i.desconto_aprovado && (
                          <Button size="sm" variant="ghost" onClick={() => { setAprovarAlvo(i); setAprovarMotivo(""); }}
                            aria-label="Aprovar desconto" data-testid={`btn-divergencia-aprovar-${i.reserva_id}`}>
                            <BadgeCheck size={14} />
                          </Button>
                        )}
                        {podeCobrar && (
                          <Button size="sm" variant="ghost" onClick={() => { setCobrarAlvo(i); setCobrarObs(""); }}
                            aria-label="Enviar cobrança por e-mail" data-testid={`btn-divergencia-cobrar-${i.reserva_id}`}>
                            <Mail size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Detalhe + histórico de cobranças */}
      <Dialog open={detalheOpen} onOpenChange={(v) => { setDetalheOpen(v); if (!v) setDetalheAlvo(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-divergencia-detalhe">
          <DialogHeader>
            <DialogTitle>Divergência da reserva</DialogTitle>
            <DialogDescription>
              Dados oficiais + histórico de cobranças enviadas. Reservas e pagamentos não são alterados por esta tela.
            </DialogDescription>
          </DialogHeader>
          {detalheAlvo && (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-3">
                <div><div className="text-[11px] uppercase text-stone-500">Hóspede</div>{detalheAlvo.hospede_nome || "—"}</div>
                <div><div className="text-[11px] uppercase text-stone-500">E-mail</div>{detalheAlvo.hospede_email || <span className="text-[#D4A652]">ausente</span>}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Documento</div><span className="font-mono text-xs">{detalheAlvo.hospede_documento || "—"}</span></div>
                <div><div className="text-[11px] uppercase text-stone-500">Empresa</div>{detalheAlvo.empresa_nome || "—"}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Pousada</div>{detalheAlvo.pousada_nome}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Entrada → Saída</div>{fmtDate(detalheAlvo.data_entrada)} → {fmtDate(detalheAlvo.data_saida)}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Atraso</div><AtrasoBadge dias={detalheAlvo.dias_atraso} /></div>
                <div><div className="text-[11px] uppercase text-stone-500">Reserva</div><span className="font-mono text-xs">{detalheAlvo.reserva_numero || detalheAlvo.reserva_id?.slice(0, 8)}…</span></div>
                <div><div className="text-[11px] uppercase text-stone-500">Contratado</div><b className="tabular-nums">{brl(detalheAlvo.valor_contratado)}</b></div>
                <div><div className="text-[11px] uppercase text-stone-500">Recebido</div><b className="tabular-nums text-[#083A6B]">{brl(detalheAlvo.valor_recebido)}</b></div>
                <div><div className="text-[11px] uppercase text-stone-500">Saldo</div><b className="tabular-nums text-[#D4A652]">{brl(detalheAlvo.saldo)}</b></div>
              </div>

              {detalheAlvo.desconto_aprovado && (
                <div className="bg-[#EAF0EA] border border-[#083A6B]/20 rounded p-3 text-xs mb-3">
                  <div className="font-medium text-[#083A6B] mb-1 flex items-center gap-1">
                    <BadgeCheck size={12} /> Desconto aprovado
                  </div>
                  <div>Por: {detalheAlvo.desconto_aprovado.aprovado_por_nome || detalheAlvo.desconto_aprovado.aprovado_por}</div>
                  <div>Em: {detalheAlvo.desconto_aprovado.aprovado_em?.slice(0, 16).replace("T", " ") || "—"}</div>
                  <div>Motivo: {detalheAlvo.desconto_aprovado.motivo}</div>
                  <div className="text-[10px] text-stone-600 mt-1">
                    Snapshot: contratado {brl(detalheAlvo.desconto_aprovado.snapshot_contratado)} · recebido {brl(detalheAlvo.desconto_aprovado.snapshot_recebido)} · saldo {brl(detalheAlvo.desconto_aprovado.snapshot_saldo)}
                  </div>
                </div>
              )}

              <div className="border-t border-stone-200 pt-3">
                <div className="text-[11px] uppercase text-stone-500 mb-2">Cobranças enviadas ({cobrancas.length})</div>
                {cobrancasLoading ? (
                  <div className="text-stone-500 text-sm">Carregando...</div>
                ) : cobrancas.length === 0 ? (
                  <div className="text-stone-500 text-sm">Nenhuma cobrança enviada.</div>
                ) : (
                  <ul className="space-y-1 text-xs text-stone-700 max-h-40 overflow-y-auto">
                    {cobrancas.map((c) => (
                      <li key={c.id} className="border border-stone-200 rounded p-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1">
                            {c.status === "sent"
                              ? <CheckCircle2 size={12} className="text-[#083A6B]" />
                              : <AlertTriangle size={12} className="text-[#D4A652]" />}
                            <b>{c.status === "sent" ? "Enviada" : "Falhou"}</b>
                            <span className="text-stone-500">para {c.destinatario}</span>
                          </div>
                          <span className="text-[10px] text-stone-500">{c.enviado_em?.slice(0, 16).replace("T", " ")}</span>
                        </div>
                        <div className="text-[10px] text-stone-500">Por {c.enviado_por_nome || c.enviado_por} · Pendente na época: {brl(c.valor_pendente)}</div>
                        {c.observacao && <div className="text-[10px] text-stone-500 mt-0.5">Obs.: {c.observacao}</div>}
                        {c.erro && <div className="text-[10px] text-[#D4A652] mt-0.5">Erro: {c.erro}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <DialogFooter className="mt-3">
                {podeAprovar && !detalheAlvo.desconto_aprovado && (
                  <Button variant="outline" onClick={() => { setAprovarAlvo(detalheAlvo); setAprovarMotivo(""); }}
                    data-testid="btn-divergencia-aprovar-dialog">
                    <BadgeCheck size={14} /> Aprovar desconto
                  </Button>
                )}
                {podeCobrar && (
                  <Button onClick={() => { setCobrarAlvo(detalheAlvo); setCobrarObs(""); }}
                    data-testid="btn-divergencia-cobrar-dialog">
                    <Mail size={14} /> Enviar cobrança
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Aprovar desconto */}
      <Dialog open={!!aprovarAlvo} onOpenChange={(v) => { if (!v) { setAprovarAlvo(null); setAprovarMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-divergencia-aprovar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BadgeCheck size={18} className="text-[#083A6B]" /> Aprovar desconto?
            </DialogTitle>
            <DialogDescription>
              Grava marcador auditável em <b>descontos_aprovados</b>. <b>Não</b> altera valor_total, pagamentos ou status da reserva. Ação exclusiva de Administrador.
            </DialogDescription>
          </DialogHeader>
          {aprovarAlvo && (
            <div className="text-sm text-stone-700 space-y-1">
              <div className="text-xs text-stone-500 truncate">{aprovarAlvo.hospede_nome} · {aprovarAlvo.pousada_nome}</div>
              <div>Saldo divergente: <b className="tabular-nums text-[#D4A652]">{brl(aprovarAlvo.saldo)}</b></div>
            </div>
          )}
          <div>
            <Label htmlFor="ap-motivo">Motivo *</Label>
            <Input id="ap-motivo" data-testid="input-divergencia-aprovar-motivo"
              value={aprovarMotivo} onChange={(e) => setAprovarMotivo(e.target.value)}
              placeholder="Descreva o motivo do desconto aprovado" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAprovarAlvo(null); setAprovarMotivo(""); }} disabled={aprovando}>
              Voltar
            </Button>
            <Button data-testid="btn-confirmar-aprovar" onClick={executarAprovar}
              disabled={aprovando || !aprovarMotivo.trim()}>
              {aprovando ? "Aprovando..." : "Confirmar aprovação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cobrar */}
      <Dialog open={!!cobrarAlvo} onOpenChange={(v) => { if (!v) { setCobrarAlvo(null); setCobrarObs(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-divergencia-cobrar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={18} className="text-[#083A6B]" /> Enviar cobrança por e-mail?
            </DialogTitle>
            <DialogDescription>
              Envia e-mail oficial ao hóspede via <b>POST /divergencias/{"{id}"}/cobrar</b>. Registra em <b>cobrancas_enviadas</b>. <b>Não</b> altera reservas, pagamentos, PagBank ou /sinal/pagar.
            </DialogDescription>
          </DialogHeader>
          {cobrarAlvo && (
            <div className="text-sm text-stone-700 space-y-1">
              <div className="text-xs text-stone-500">{cobrarAlvo.hospede_nome}</div>
              <div>Destinatário: <b>{cobrarAlvo.hospede_email || <span className="text-[#D4A652]">ausente</span>}</b></div>
              <div>Saldo pendente: <b className="tabular-nums text-[#D4A652]">{brl(cobrarAlvo.saldo)}</b></div>
              {(!cobrarAlvo.hospede_email || !cobrarAlvo.hospede_email.includes("@")) && (
                <div className="text-[11px] text-[#7c3a20] bg-[#F5E4DD] border border-[#D4A652]/25 rounded px-2 py-1 mt-1">
                  E-mail ausente ou inválido. Atualize o cadastro antes de cobrar.
                </div>
              )}
            </div>
          )}
          <div>
            <Label htmlFor="cob-obs">Observação (opcional)</Label>
            <Input id="cob-obs" data-testid="input-divergencia-cobrar-obs"
              value={cobrarObs} onChange={(e) => setCobrarObs(e.target.value)}
              placeholder="Comentário interno registrado no envio" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCobrarAlvo(null); setCobrarObs(""); }} disabled={cobrando}>
              Voltar
            </Button>
            <Button data-testid="btn-confirmar-cobrar" onClick={executarCobrar}
              disabled={cobrando || !cobrarAlvo?.hospede_email || !cobrarAlvo?.hospede_email?.includes("@")}>
              {cobrando ? "Enviando..." : "Confirmar envio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
